// @ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import {
    FaCheckCircle,
    FaSync,
    FaClipboardList,
    FaUtensils,
    FaBoxOpen,
    FaStar
} from "react-icons/fa";
import { useSelector } from "react-redux";
import { useLanguageContext } from "../../context/languageContext";
import { useNavigate } from "react-router-dom";
import { ReviewModal } from "../../components/ReviewModal";

const OrderStatus = () => {
    const navigate = useNavigate();
    const { t } = useLanguageContext();

    const ordersArray = useSelector((state) => state.details.orders || []);
    const businessId = useSelector((state) => state.business.businessId) || '2';

    const [showConfirmIds, setShowConfirmIds] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasMounted, setHasMounted] = useState(false);
    const [reviewModalOpen, setReviewModalOpen] = useState(false);
    const [selectedOrderId, setSelectedOrderId] = useState(null);

    // Which orders have already had their "Order Sent" flash. A ref, not state: the
    // effect below has to read it without listing it as a dependency - depending on it
    // would re-run the effect the moment it changes, and the cleanup would cancel the
    // very timers that clear the flash, leaving the overlay up for good.
    const flashedOrderIds = useRef(new Set());

    useEffect(() => {
        if (!hasMounted && ordersArray.length > 0) {
            setHasMounted(true);
            return undefined;
        }
        if (!hasMounted) return undefined;

        const arrived = ordersArray.filter((order) => !flashedOrderIds.current.has(order.id));
        if (arrived.length === 0) return undefined;

        arrived.forEach((order) => flashedOrderIds.current.add(order.id));
        setShowConfirmIds((prev) => [...prev, ...arrived.map((order) => order.id)]);

        const timers = arrived.map((order) =>
            setTimeout(() => {
                setShowConfirmIds((prev) => prev.filter((id) => id !== order.id));
            }, 2500)
        );
        return () => timers.forEach(clearTimeout);
    }, [ordersArray, hasMounted]);

    const fetchAllOrders = async () => {
        setLoading(true);
        try {
            // Simplified fetch logic for now
            // In a real app, this would be a single endpoint call
        } catch (error) {
            console.error("Error refreshing orders:", error);
        } finally {
            setLoading(false);
        }
    };

    const StatusBadge = ({ status }) => {
        const s = status?.toLowerCase() || '';
        let colors = 'bg-slate-50 text-slate-600 border-slate-200';
        if (s.includes('pend')) colors = 'bg-amber-50 text-amber-700 border-amber-100';
        if (s.includes('prepar') || s.includes('cook')) colors = 'bg-indigo-50 text-indigo-700 border-indigo-100';
        if (s.includes('serv') || s.includes('complet')) colors = 'bg-emerald-50 text-emerald-700 border-emerald-100';

        return (
            <span className={`px-2 py-0.5 rounded-md text-[10px] font-bold uppercase tracking-wider border ${colors}`}>
                {status}
            </span>
        );
    };

    if (ordersArray.length === 0) {
        return (
            <div className="main-content !pb-0 bg-white flex items-center justify-center p-6 text-sharp">
                <div className="w-full max-w-sm text-center space-y-6 animate-app-reveal">
                    <div className="mx-auto w-20 h-20 flex items-center justify-center rounded-2xl bg-slate-50 border border-slate-100">
                        <FaUtensils className="text-2xl text-slate-300" />
                    </div>
                    <div className="space-y-2">
                        <h2 className="text-2xl font-clash text-slate-900 tracking-tight">{t("No orders yet")}</h2>
                        <p className="text-sm text-slate-500 font-medium leading-relaxed px-8">
                            {t("Your order history is empty. Discover our curated menu and start your culinary journey.")}
                        </p>
                    </div>
                    <button
                        onClick={() => navigate(`/home?businessid=${businessId}`)}
                        className="btn-app btn-primary-app w-full text-sm tracking-widest uppercase"
                    >
                        {t("Menu")}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="flex-1 bg-white">
            <div className="page-padding py-6 max-w-2xl mx-auto space-y-6">

                {/* Header */}
                <div className="flex items-end justify-between border-b border-slate-100 pb-4">
                    <div className="space-y-0.5">
                        <h1 className="text-lg font-clash text-slate-900 tracking-tight">{t("Order Status")}</h1>
                        <p className="text-xs font-black uppercase tracking-[0.2em] text-slate-400">{t("Live Kitchen Feed")}</p>
                    </div>
                    <button
                        onClick={fetchAllOrders}
                        className="text-indigo-600 p-2 active:scale-90 transition-all opacity-40 hover:opacity-100"
                    >
                        <FaSync size={14} className={loading ? 'animate-spin' : ''} />
                    </button>
                </div>

                {/* Orders List */}
                <div className="space-y-4">
                    {ordersArray.map((order, idx) => (
                        <div
                            key={order.id}
                            className="bg-white border border-slate-100 rounded-2xl overflow-hidden animate-app-reveal relative"
                            style={{ animationDelay: `${idx * 0.05}s` }}
                        >
                            <div className="p-4 space-y-4">
                                <div className="flex items-center justify-between">
                                    <div className="flex items-center gap-2">
                                        <span className="text-xs font-black text-slate-400">#{order.id}</span>
                                        <StatusBadge status={order.orderStatus} />
                                    </div>
                                    <div className="text-[10px] font-black text-slate-900">{order.total} ALL</div>
                                </div>

                                <div className="space-y-2">
                                    {order.menuItems?.map((item, i) => (
                                        <div key={i} className="flex items-center justify-between">
                                            <div className="flex items-center gap-3">
                                                <div className="w-5 h-5 rounded bg-slate-50 border border-slate-100 flex items-center justify-center text-xs font-black text-slate-400">
                                                    {item.amount}
                                                </div>
                                                <span className="text-sm font-bold text-slate-800">{item.name}</span>
                                            </div>
                                            <span className="text-[11px] font-bold text-slate-400">{item.price} ALL</span>
                                        </div>
                                    ))}
                                </div>

                                {/* Order Logistics */}
                                <div className="pt-3 border-t border-slate-50 flex items-center justify-between text-[11px]">
                                    <div className="flex items-center gap-4 text-slate-400">
                                        <span className="flex items-center gap-1.5"><FaClipboardList size={10} /> Table {order.tableNo}</span>
                                    </div>
                                    <div className="font-bold text-indigo-600 uppercase tracking-tighter">{t("Kitchen Notified")}</div>
                                </div>

                                {(order.orderStatus?.toLowerCase().includes('serv') || order.orderStatus?.toLowerCase().includes('complet')) && (
                                    <button
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            setSelectedOrderId(order.id);
                                            setReviewModalOpen(true);
                                        }}
                                        className="w-full py-2.5 mt-2 bg-slate-50 border border-slate-100 rounded-xl text-[10px] font-black uppercase tracking-widest text-slate-600 hover:bg-indigo-50 hover:text-indigo-600 hover:border-indigo-100 transition-all flex items-center justify-center gap-2"
                                    >
                                        <FaStar className="text-amber-400" />
                                        {t("Leave Review")}
                                    </button>
                                )}
                            </div>

                            {/* Success Overlay */}
                            {showConfirmIds.includes(order.id) && (
                                <div className="absolute inset-0 bg-slate-900/95 z-20 flex flex-col items-center justify-center text-white animate-fade-in shadow-2xl">
                                    <FaCheckCircle className="text-emerald-400 w-8 h-8 mb-2 animate-bounce" />
                                    <p className="text-xs font-black uppercase tracking-[0.3em]">{t("Order Sent")}</p>
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                {/* Status Help */}
                <div className="p-5 rounded-2xl bg-slate-50 border border-slate-100 flex items-center gap-4">
                    <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center text-slate-300">
                        <FaBoxOpen size={16} />
                    </div>
                    <p className="text-xs text-slate-500 font-medium leading-relaxed">
                        {t("Track your culinary journey in real-time. Statuses update as our chef moves through your order.")}
                    </p>
                </div>
            </div>

            <ReviewModal
                isOpen={reviewModalOpen}
                onClose={() => setReviewModalOpen(false)}
                type="ORDER"
                orderId={selectedOrderId}
            />
        </div>
    );
};

export default OrderStatus;
